"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Search, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function AffordableHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="affordableseohero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Subtle cobalt-blue radial gradient decoration */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
        <div className="size-[650px] rounded-full bg-primary/5 blur-[120px] md:size-[850px]" />
        <div className="absolute top-0 right-10 size-[320px] rounded-full bg-secondary/60 blur-[90px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mx-auto max-w-4xl text-center">
          {/* Eyebrow Badge */}
          <motion.div variants={itemVariants} className="mb-6 flex justify-center">
            <Badge variant="outline" className="gap-2 rounded-full border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary shadow-xs">
              <Sparkles className="size-3.5 text-accent" />
              <span>Affordable, Professional SEO</span>
            </Badge>
          </motion.div>

          {/* Single H1 Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Affordable SEO Services UK
          </motion.h1>

          {/* First-person supporting content */}
          <motion.div variants={itemVariants} className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            <p className="font-sans font-normal">
              Affordable SEO is not the same as cheap SEO — it means appropriate scope,
              prioritisation, and transparent investment rather than trying to do everything
              at once. I provide affordable SEO services focused on prioritised, high-impact
              search strategy, clear deliverables, and transparent communication tailored
              directly to your business goals.
            </p>
            <p className="font-sans font-normal">
              Over 90% of online experiences begin with a search engine, and over 3.5
              billion searches occur on Google daily. If your business isn&apos;t visible in
              search results, you&apos;re losing customers to competitors who are.
              Affordable SEO typically means focused plans rather than trying to do
              everything at once – prioritising the work that moves the needle for your
              specific business, industry, and budget.
            </p>
          </motion.div>

          {/* Direct Consultant Value Props */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-foreground/80">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Transparent pricing</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Tailored plan for your budget</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-primary" />
              <span>No long-term lock-in contracts</span>
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="w-full rounded-md bg-primary px-8 text-primary-foreground shadow-md transition-colors hover:bg-primary/90 sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center gap-2 font-medium">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full rounded-md border-border bg-background px-8 text-foreground shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground sm:w-auto">
              <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2 font-medium">
                <Search className="size-4" />
                <span>Get a Free SEO Audit</span>
              </Link>
            </Button>
          </motion.div>

          {/* Trust Micro-Note */}
          <motion.p variants={itemVariants} className="mt-6 text-xs text-muted-foreground sm:text-sm">
            Ready to improve your search rankings without the agency price tag? Book a free
            SEO consultation and find out exactly what your website needs.
          </motion.p>
        </motion.div>
      </div>
    </section>;
}